import { AuthModel } from '../modules/auth/core/_models'

/**
 * Secure Storage Utility - Functional Implementation
 * Provides secure token storage using httpOnly cookies as the primary method
 * with sessionStorage as fallback for development/compatibility
 */

interface SecureStorageConfig {
  useHttpOnlyCookies: boolean;
  cookieDomain: string;
  cookiePath: string;
  secure: boolean;
  sameSite: 'strict' | 'lax' | 'none';
}

const AUTH_COOKIE_NAME = 'b4igo_auth_token';
const AUTH_STORAGE_KEY = 'auth';

// Configuration based on environment
const getStorageConfig = (): SecureStorageConfig => ({
  useHttpOnlyCookies: process.env.NODE_ENV === 'production',
  cookieDomain: window.location.hostname,
  cookiePath: '/',
  secure: process.env.NODE_ENV === 'production' || window.location.protocol === 'https:',
  sameSite: 'strict'
});

/**
 * Check if we can use secure cookies
 */
const canUseSecureCookies = (): boolean => {
  return window.isSecureContext || window.location.hostname === 'localhost';
};

/**
 * Set authentication data securely
 */
export const setAuth = (auth: AuthModel): void => {
  if (!auth) return;

  try {
    const config = getStorageConfig();

    if (config.useHttpOnlyCookies && canUseSecureCookies()) {
      setSecureCookie(auth, config);
    } else {
      // Use localStorage as fallback for session persistence across page reloads
      setLocalStorage(auth);
    }
  } catch (error) {
    // Fallback to localStorage on any error
    setLocalStorage(auth);
  }
};

/**
 * Get authentication data
 */
export const getAuth = (): AuthModel | undefined => {
  try {
    const config = getStorageConfig();

    if (config.useHttpOnlyCookies && canUseSecureCookies()) {
      return getFromSecureCookie();
    } else {
      return getFromLocalStorage();
    }
  } catch (error) {
    return undefined;
  }
};

/**
 * Remove authentication data completely
 */
export const removeAuth = (): void => {
  try {
    // Clear all possible storage locations
    removeSecureCookie();
    removeFromLocalStorage();
    removeFromSessionStorage(); // Clean up any session data
  } catch (error) {
    // Silently handle errors - cleanup should continue
  }
};

/**
 * Get API token for requests
 */
export const getApiToken = (): string | undefined => {
  const auth = getAuth();
  return auth?.token;
};

/**
 * Private helper functions
 */
const setSecureCookie = (auth: AuthModel, config: SecureStorageConfig): void => {
  const authData = JSON.stringify(auth);

  const cookieOptions = [
    `path=${config.cookiePath}`,
    config.secure ? 'secure' : '',
    `SameSite=${config.sameSite}`,
    // Set expiration to 30 days
    `max-age=${30 * 24 * 60 * 60}`
  ].filter(Boolean).join('; ');

  // Note: True HttpOnly cookies must be set server-side
  // This provides enhanced security compared to localStorage
  document.cookie = `${AUTH_COOKIE_NAME}=${encodeURIComponent(authData)}; ${cookieOptions}`;
};

const getFromSecureCookie = (): AuthModel | undefined => {
  const cookies = document.cookie.split(';');
  const authCookie = cookies.find(cookie =>
    cookie.trim().startsWith(`${AUTH_COOKIE_NAME}=`)
  );

  if (!authCookie) {
    return undefined;
  }

  try {
    const authData = decodeURIComponent(authCookie.split('=')[1]);
    return JSON.parse(authData) as AuthModel;
  } catch (error) {
    return undefined;
  }
};

const removeSecureCookie = (): void => {
  const config = getStorageConfig();
  document.cookie = `${AUTH_COOKIE_NAME}=; path=${config.cookiePath}; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=${config.sameSite}${config.secure ? '; secure' : ''}`;
};

const setLocalStorage = (auth: AuthModel): void => {
  if (!localStorage) {
    throw new Error('LocalStorage not available');
  }

  const authData = JSON.stringify(auth);
  localStorage.setItem(AUTH_STORAGE_KEY, authData);
};

const getFromLocalStorage = (): AuthModel | undefined => {
  if (!localStorage) {
    return undefined;
  }

  const authData = localStorage.getItem(AUTH_STORAGE_KEY);
  if (!authData) {
    return undefined;
  }

  try {
    return JSON.parse(authData) as AuthModel;
  } catch (error) {
    return undefined;
  }
};

const removeFromSessionStorage = (): void => {
  if (sessionStorage) {
    sessionStorage.removeItem(AUTH_STORAGE_KEY);
  }
};

const removeFromLocalStorage = (): void => {
  if (localStorage) {
    localStorage.removeItem(AUTH_STORAGE_KEY);
    localStorage.removeItem('persist:root'); // Clean up persisted Redux store
  }
};

// Legacy support - export the auth key for backward compatibility
export const AUTH_LOCAL_STORAGE_KEY = AUTH_STORAGE_KEY;